import type { RestoredTaProposal, TechnicalAuditProposal } from './taProposalTypes';

export type CentralChatContextType =
  | 'global'
  | 'wordpress'
  | 'editor_article'
  | 'template_builder'
  | 'service_page'
  | 'section'
  | 'technical_audit_issue';

export interface CentralChatIssueAttachment {
  issue_id: string;
  issue_type: string;
  page_title: string | null;
  wordpress_id?: number | null;
  run_id?: string | null;
}

export interface CentralChatCapabilities {
  can_use_wordpress: boolean;
  can_edit_article: boolean;
  can_generate_service_page: boolean;
  can_render_arrow_ui: boolean;
  can_use_web_research: boolean;
  can_generate_images: boolean;
}

export interface CentralChatContext {
  context_type: CentralChatContextType;
  tenant_id: string | null;
  route: string;
  content_id?: string;
  template_id?: string;
  section_id?: string;
  issue_id?: string;
  run_id?: string;
  wordpress_id?: number | null;
  page_url?: string;
  selected_text?: string;
  editor_snapshot?: Record<string, unknown>;
  capabilities: CentralChatCapabilities;
  inputs?: Record<string, unknown>;
  payload?: Record<string, unknown>;
}

export type ToolActivityStatus = 'running' | 'done' | 'error' | 'stopped';

export interface WriteApproval {
  requestId: string;
  toolName: string;
  postId?: string | number | null;
  field: string;
  before?: unknown;
  after?: unknown;
}

/** The New client card Emjae parks before it adds a client (what the backend's `build_client_card`
 *  sends, in camelCase). Only what the card shows: the existing client's ids, the conflict text and
 *  the raw arguments never reach the UI. */
export interface NewClientApproval {
  kind: 'new_client';
  requestId: string;
  toolName: string;
  client: { name: string; website: string; services: 'seo' | 'ads' | 'both'; approvalRequired: boolean };
  /** People who get access, by name. The id is carried but never shown. */
  members: Array<{ id: string; name: string }>;
  unmatchedMemberCount: number;
  preview: {
    website: string;
    willCreate: { tenant: boolean; workspace: boolean };
    tenantExists: boolean;
    workspaceExists: boolean;
  };
  /** True when the card cannot be approved (a conflict, or an ads-armed website): Decline only. */
  declineOnly: boolean;
  notice: string | null;
  dryRun: boolean;
}

/** The card Emjae parks before it offboards or reactivates a client: who, and the status change. */
export type ClientStatusKind = 'offboard_client' | 'reactivate_client';
export type ClientStatus = 'active' | 'paused' | 'offboarded';

export interface ClientStatusApproval {
  kind: ClientStatusKind;
  requestId: string;
  toolName: string;
  client: { name: string | null; website: string };
  /** Null when the client could not be found. */
  statusBefore: ClientStatus | null;
  statusAfter: ClientStatus | null;
  /** True when the card cannot be approved (unknown client, or already in that state): Decline only. */
  declineOnly: boolean;
  notice: string | null;
  dryRun: boolean;
}

/** One previewed change on the Agile Plan card, as `planner_actions.preview_changes` returned it (camelCase). */
export interface AgilePlanRow {
  index: number;
  kind: string;
  valid: boolean;
  errors: string[];
  noop: boolean;
  target: unknown;
  labels: { client?: string | null; task_type?: string | null; billing_period?: string | null };
  changes: Array<{ field: string; from: unknown; to: unknown }>;
  before: unknown;
  after: unknown;
  /** Notes rows: the text removed and the text added. */
  diff: { removed?: string; added?: string } | null;
  teamworkIdentity: string | null;
  teamworkIdentityLabel: string | null;
}

/** The ONE card Emjae shows for a set of Agile Plan changes: a row per change, field by field. The rows are
 *  stored server-side with the park; Approve applies exactly those. */
export interface AgilePlanApproval {
  kind: 'agile_plan_changes';
  requestId: string;
  toolName: string;
  client: { website: string };
  rows: AgilePlanRow[];
  summary: { total: number; toApply: number; invalid: number; noop: number };
  dryRun: boolean;
  createdAt: string;
  expiresAt: string;
  declineOnly: boolean;
  notice: string | null;
}

/** What happened to one row after Approve. */
export interface AgilePlanRowResult {
  index: number;
  kind?: string;
  status: 'done' | 'skipped' | 'failed' | string;
  reason?: string | null;
}

export type AnyWriteApproval = WriteApproval | NewClientApproval | ClientStatusApproval | AgilePlanApproval;

export function isAgilePlanApproval(card: AnyWriteApproval | undefined | null): card is AgilePlanApproval {
  return (card as AgilePlanApproval | undefined | null)?.kind === 'agile_plan_changes';
}

export function isClientStatusApproval(card: AnyWriteApproval | undefined | null): card is ClientStatusApproval {
  const kind = (card as ClientStatusApproval | undefined | null)?.kind;
  return kind === 'offboard_client' || kind === 'reactivate_client';
}

export function isNewClientApproval(card: AnyWriteApproval | undefined | null): card is NewClientApproval {
  return Boolean(card) && (card as NewClientApproval).kind === 'new_client';
}

export interface ToolActivityEntry {
  name: string;
  status: ToolActivityStatus;
  /** Why a row failed or was cut, in words. Only ever set on error/stopped
   *  rows: a finished tool needs no excuse. */
  detail?: string;
  writeApproval?: AnyWriteApproval;
}

/**
 * How a turn ended, which is what its settled trace label has to say.
 * `awaiting` is the parked case: the transport said `complete` because it had
 * nothing left to send, but the turn is a question, not an answer.
 */
export interface EmjaeQuestionOption {
  id: string;
  label: string;
  description?: string;
  style?: string;
}

/** A question Emjae asked and is waiting on. Answering goes through the answer path, never a new turn. */
export interface EmjaeQuestion {
  requestId: string;
  options: EmjaeQuestionOption[];
  allowFreeform: boolean;
}

export type TurnOutcome = 'done' | 'error' | 'stopped' | 'capped' | 'awaiting';

// ── Client-executed page tools ─────────────────────────────────────────
//
// Eve can hand a tool call back to the BROWSER instead of resolving it
// server-side: the page executes it and posts the result up. These types are
// the client half of that seam. The WebMCP lane plugs execution and transport
// in through clientToolBridge.ts; nothing in the chat's rendering knows or
// cares who executed a call.

export type ClientToolStatus = 'pending' | 'resolved' | 'expired';

export interface ClientToolCall {
  /** Eve's tool_use id — the correlation key the result must echo. */
  id: string;
  name: string;
  args: Record<string, unknown>;
  issuedAt: number;
  status: ClientToolStatus;
}

/**
 * What goes back up the wire for a call the page never answered. `expired` is
 * terminal and never retried: a second answer for the same id is dropped.
 */
export interface ClientToolResult {
  tool_use_id: string;
  status: 'success' | 'error';
  /** Machine-readable reason when status is 'error'. */
  error?: 'client_tool_timeout' | 'client_tool_orphaned' | 'client_tool_failed';
  content?: unknown;
  message?: string;
}

/**
 * One tool the PAGE can run, as the chat request carries it. Snake case
 * because it crosses the wire into `PageToolDescriptor` in
 * `api/schemas/chat_hub.py`. Built by the WebMCP lane; the chat only forwards
 * it and never reads inside `input_schema`.
 */
export interface PageToolDescriptor {
  readonly name: string;
  readonly description: string;
  /** A JSON string, exactly as the browser reported it. */
  readonly input_schema: string;
}

export interface CentralChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
  uiSource?: string;
  actions?: ChatHubAction[];
  error?: string;
  // Tappable quick-reply suggestions for an intake question (e.g. proposal
  // flow): clicking one submits its text as the next reply.
  quickReplies?: string[];
  // A generated proposal's id — the bubble renders an inline branded-report
  // preview + Download-PDF button for it.
  reportProposalId?: string;
  // Set when the agent parked the turn on a person instead of finishing it, so
  // a question does not render as a completed answer. Carries the Eve event
  // kind (`input.requested` / `authorization.required`).
  awaiting?: string;
  writeApproval?: AnyWriteApproval;
  // After an Agile Plan card is approved: what happened to each row, and Emjae's first-person summary.
  agilePlanResults?: AgilePlanRowResult[];
  agilePlanResultText?: string;
  // Set the moment an approved Agile Plan card starts settling: no second Approve or Cancel can be sent, and the card shows no live buttons.
  settling?: boolean;
  // Emjae's own unanswered question (ask_question): the prompt is `content`, these are its choices.
  question?: EmjaeQuestion;
  // The run this answer was built from, kept on the message rather than in the
  // provider's live state so a finished turn's trace survives the next one.
  // Written once, when the turn settles.
  trace?: ToolActivityEntry[];
  traceDurationMs?: number;
  traceOutcome?: TurnOutcome;
  taProposal?: TechnicalAuditProposal;
  taProposalRestored?: RestoredTaProposal;
}

export interface ChatHubAction {
  id: string;
  kind: 'clarify' | 'api_action' | 'workflow' | 'navigate' | 'handoff';
  action_kind: string;
  label: string;
  description?: string | null;
  payload: Record<string, unknown>;
  choices?: Array<{
    id: string;
    label: string;
    value: string;
    payload?: Record<string, unknown>;
  }>;
  requires_confirmation: boolean;
  risk: 'read' | 'draft' | 'write' | 'publish' | 'destructive';
  status:
    | 'pending_user'
    | 'approval_requested'
    | 'approved'
    | 'rejected'
    | 'edited'
    | 'running'
    | 'completed'
    | 'expired'
    | 'error';
  resume_token?: string | null;
  expires_at?: string | null;
}

export interface ChatHubStreamEvent {
  type:
    | 'init'
    | 'thinking'
    | 'tool_start'
    | 'tool_result'
    | 'state'
    | 'action_requested'
    | 'chunk'
    | 'complete'
    | 'error'
    | 'ui'
    | string;
  message?: string;
  content?: string;
  conversation_id?: string;
  progress?: number;
  // `agile_plan_results` only: what happened to each row of an approved Agile Plan card (message is the summary).
  results?: unknown[];
  data?: {
    source?: string;
    ui_source?: string;
    navigate_to?: string;
    actions?: ChatHubAction[];
    working_with?: Record<string, unknown>;
    thread_id?: string;
    checkpoint_backend?: string;
    tool_name?: string;
    // `tool_result` only: how the tool ended, e.g. "completed" or "error".
    status?: string;
    // `tool_result` only, failures only: the reason, already truncated by the
    // backend. "failed" alone is not honest enough to act on.
    error_detail?: string;
    // `thinking` only: the event is a synthetic keep-alive, not agent progress.
    heartbeat?: boolean;
    // `complete` only: the turn is parked on a person, not finished.
    awaiting?: string;
    // Any event: a tool call handed to the BROWSER to execute. Additive — a
    // backend that never sends it changes nothing. See clientToolBridge.ts.
    client_tool_use?: { id: string; name: string; args?: Record<string, unknown> };
    // `complete` only: the backend cut the turn at its own time limit and sent
    // whatever had arrived. A short answer, not a failure.
    stopped_by?: string;
    limit_secs?: number;
    metadata?: Record<string, unknown>;
    [key: string]: unknown;
  };
}

export interface ChatHubConversation {
  id: string;
  tenant_id: string;
  title?: string | null;
  context_type?: string | null;
  route?: string | null;
  content_id?: string | null;
  template_id?: string | null;
  section_id?: string | null;
  message_count: number;
  last_message_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface ChatHubConversationDetail extends ChatHubConversation {
  messages: Array<{
    id: string;
    role: string;
    content: string;
    created_at?: string | null;
    proposal_status?: string | null;
    proposal_data?: unknown;
  }>;
  pending_question?: Record<string, unknown> | null;
  pending_write_approval?: {
    request_id: string;
    tool_name: string;
    post_id?: string | number | null;
    field?: string;
    before?: unknown;
    after?: unknown;
  } | null;
}
